import {
  createContext,
  memo,
  useCallback,
  useContext,
  useEffect,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { inlineLogoForTerm } from "./inline-logos/rehypeInlineLogos";
import { conceptMarkForTerm } from "./inline-logos/conceptMarks";
import { ConceptSvg } from "./TechMark";

/**
 * 本文の用語 (点線の下線) と、その詳細の窓。
 *
 * 出し方
 * - マウスは語か「?」に乗ると少し遅れて出て、窓の上へ指が移っても消えない。外れると少しして消える
 * - キーボードは語に focus が来ると出る。Esc で閉じる
 * - 触る端末 (hover が無い) と狭い画面は、語か「?」を押して開く (語を押しても外の頁へ飛ばない)。外を押すと閉じる
 * - 窓の外から stayInPlace を渡された所 (学習ポータルの本文) は、マウスでも語を押すと窓を開く。辞書へは窓の「くわしく見る」から新しいタブで行く
 * - 窓は語のすぐ後ろに置き (Tab で窓の中のリンクへ進める)、位置は画面に固定して計算する。右端でもはみ出さない
 */

/** 関連章の 1 つ。label は人の言葉の章の名前、href があれば学習パスの章へ飛べる */
export type GlossaryChapterRef = { label: string; href?: string };

/** 窓の外から渡す物。無ければ、章の欄は出さず、字の折りは CSS の文節折りだけに任せる */
export type GlossaryCardEnv = {
  /** 「Ch28」の 28 から、学習パスの章の名前と住所を引く。引けなければ null (その章は出さない) */
  chapterOf?: (chapterNumber: number) => GlossaryChapterRef | null;
  /** 字を文節の境目にだけ折れる形にする (LgPhrase の phraseChildren) */
  phrase?: (children: ReactNode) => ReactNode;
  /**
   * 語を押しても辞書の頁へ移らず、その場で窓を開く (外枠と読んでいた位置を失わない)。
   * 窓から辞書へ出る道は「くわしく見る」の 1 つだけにし、新しいタブで開く。関連用語は札にする
   */
  stayInPlace?: boolean;
};

export const GlossaryCardEnvContext = createContext<GlossaryCardEnv>({});

/** 開くまで、閉じるまでの待ち (ms)。出るのは少し遅く、消えるのは早く */
const OPEN_DELAY = 180;
const CLOSE_DELAY = 140;
/** 消える動き (CSS の --gl-out-dur と同じ長さ) の間だけ、窓を残す */
const UNMOUNT_AFTER = 80;

const useIsoLayoutEffect = typeof window === "undefined" ? useEffect : useLayoutEffect;

type Phase = "closed" | "open" | "closing";

type RelatedTerm = { name: string; slug?: string };

function parseRelatedTerms(raw: string | undefined): RelatedTerm[] {
  if (!raw) return [];
  return raw
    .split("|")
    .filter(Boolean)
    .map((item) => {
      const at = item.indexOf("@@");
      return at < 0 ? { name: item } : { name: item.slice(0, at), slug: item.slice(at + 2) || undefined };
    });
}

/** 「Ch28, Ch55」の章番号。「Ch7-3」のような実践プロジェクトの小さな章は学習パスの番号ではないので拾わない */
function parseChapterNumbers(raw: string | undefined): number[] {
  if (!raw) return [];
  const out: number[] = [];
  for (const m of raw.matchAll(/Ch(\d+)(?![\d-])/g)) {
    const n = Number(m[1]);
    if (!out.includes(n)) out.push(n);
  }
  return out;
}

/** 日本語を含まない別名は英語の綴りとして並べ、残りは別の呼び方にする */
function splitAliases(term: string, aliases: readonly string[] | undefined) {
  const seen = new Set([term.toLowerCase()]);
  const english: string[] = [];
  const other: string[] = [];
  const termIsJapanese = /[぀-ヿ㐀-鿿]/.test(term);
  for (const alias of aliases ?? []) {
    const key = alias.trim().toLowerCase();
    if (!key || seen.has(key)) continue;
    seen.add(key);
    if (termIsJapanese && /^[\x20-\x7e]+$/.test(alias)) english.push(alias);
    else other.push(alias);
  }
  return { english, other };
}

// ---------------------------------------------------------------------------
// 詳しい説明 (軽い markdown だけ)
// ---------------------------------------------------------------------------

const DETAIL_LIMIT = 520;

/** 長い説明は段落の切れ目で止める (字の途中や、コードの途中で切らない) */
function clipDetail(markdown: string): { text: string; clipped: boolean } {
  if (markdown.length <= DETAIL_LIMIT) return { text: markdown, clipped: false };
  const blocks = markdown.split(/\n{2,}/);
  let text = "";
  for (const block of blocks) {
    const next = text ? `${text}\n\n${block}` : block;
    if (next.length > DETAIL_LIMIT) break;
    text = next;
  }
  if (text) return { text, clipped: true };
  const head = markdown.slice(0, DETAIL_LIMIT);
  const stop = Math.max(head.lastIndexOf("。"), head.lastIndexOf("\n"));
  return { text: stop > 80 ? head.slice(0, stop + 1) : head, clipped: true };
}

function InlineMarkdown({ text }: { text: string }) {
  const tokens: ReactNode[] = [];
  const regex = /(==[^=\n]+==|\*\*[^*\n]+\*\*|`[^`\n]+`)/g;
  let last = 0;
  let i = 0;
  let m: RegExpExecArray | null;
  while ((m = regex.exec(text)) !== null) {
    if (m.index > last) tokens.push(text.slice(last, m.index));
    const t = m[0];
    if (t.startsWith("==")) tokens.push(<mark key={i++}>{t.slice(2, -2)}</mark>);
    else if (t.startsWith("**")) tokens.push(<strong key={i++}>{t.slice(2, -2)}</strong>);
    else tokens.push(<code key={i++} className="gl-card__ic">{t.slice(1, -1)}</code>);
    last = m.index + t.length;
  }
  if (last < text.length) tokens.push(text.slice(last));
  return <>{tokens}</>;
}

function DetailBody({ markdown }: { markdown: string }) {
  const parts: Array<{ type: "code" | "text"; value: string }> = [];
  const codeRegex = /```(\w*)\n([\s\S]*?)```/g;
  let lastIndex = 0;
  let m: RegExpExecArray | null;
  while ((m = codeRegex.exec(markdown)) !== null) {
    if (m.index > lastIndex) parts.push({ type: "text", value: markdown.slice(lastIndex, m.index) });
    parts.push({ type: "code", value: m[2] });
    lastIndex = m.index + m[0].length;
  }
  if (lastIndex < markdown.length) parts.push({ type: "text", value: markdown.slice(lastIndex) });
  return (
    <>
      {parts.map((p, i) =>
        p.type === "code" ? (
          <span key={i} className="gl-card__code">
            {p.value}
          </span>
        ) : (
          <InlineMarkdown key={i} text={p.value} />
        ),
      )}
    </>
  );
}

// ---------------------------------------------------------------------------
// 窓
// ---------------------------------------------------------------------------

type CardProps = {
  id: string;
  titleId: string;
  term: string;
  matched?: string;
  reading?: string;
  aliases?: readonly string[];
  category?: string;
  difficulty?: number;
  summary?: string;
  detail?: string;
  related?: string;
  relatedTerms: RelatedTerm[];
  slug?: string;
  closing: boolean;
  anchorRef: { current: HTMLElement | null };
  /** 板の閉じるボタン */
  onClose: () => void;
};

/** 窓の題の絵は、正式ロゴ、考え方の印、マスコットの順。印のある語はマスコットと並べず、印に置き換える */
function Mark({ name, matched }: { name: string; matched?: string }) {
  const logo = useMemo(() => inlineLogoForTerm(name, matched), [name, matched]);
  const concept = useMemo(() => (logo ? null : conceptMarkForTerm(name, matched)), [logo, name, matched]);
  const [characterBroken, setCharacterBroken] = useState(false);
  if (logo) {
    return (
      <span className="gl-card__logo" aria-hidden="true">
        <svg
          className={logo.aws ? "ilogo ilogo--aws" : "ilogo"}
          data-ilogo={logo.id}
          width={`${logo.aws ? +(logo.ratio * 1.2).toFixed(4) : logo.ratio}em`}
          height="1em"
          focusable="false"
        >
          <use href={logo.href} />
        </svg>
      </span>
    );
  }
  if (concept) {
    return (
      <span className="gl-card__logo gl-card__logo--concept" aria-hidden="true">
        <ConceptSvg mark={concept} />
      </span>
    );
  }
  // 画像が読めないとき (住所が無い、壊れている) は枠ごと出さない
  if (characterBroken) return null;
  return (
    <img
      className="gl-card__character"
      src="/glossary-study.svg"
      alt=""
      aria-hidden="true"
      width={36}
      height={36}
      onError={() => setCharacterBroken(true)}
    />
  );
}

/** ゆめさくチューターのふくろう (AiTutorPanel の先生の顔と同じ原画)。題の行の右端に小さく添える飾りなので、読み上げには出さない */
const OWL_SRC = "/mascot/fukurou-01.svg";
function Owl({ size }: { size: number }) {
  const [broken, setBroken] = useState(false);
  if (broken) return null;
  return (
    <span className="gl-card__owl" aria-hidden="true">
      <img src={OWL_SRC} alt="" width={Math.round((size * 336) / 327)} height={size} onError={() => setBroken(true)} />
    </span>
  );
}

/** 関連用語の札の頭に付ける小さな印。印のある語だけ (正式ロゴか考え方の印)。無い語は字だけ */
function ChipMark({ name }: { name: string }) {
  const logo = useMemo(() => inlineLogoForTerm(name), [name]);
  const concept = useMemo(() => (logo ? null : conceptMarkForTerm(name)), [logo, name]);
  if (logo) {
    return (
      <span className="gl-card__chip-mark" aria-hidden="true">
        <svg
          className={logo.aws ? "ilogo ilogo--aws" : "ilogo"}
          data-ilogo={logo.id}
          width={`${logo.aws ? +(logo.ratio * 1.2).toFixed(4) : logo.ratio}em`}
          height="1em"
          focusable="false"
        >
          <use href={logo.href} />
        </svg>
      </span>
    );
  }
  if (!concept) return null;
  return (
    <span className="gl-card__chip-mark" aria-hidden="true">
      <ConceptSvg mark={concept} />
    </span>
  );
}

// 狭い画面と、hover の無い端末では、窓を画面の下から出る板 (bottom sheet) にする
const SHEET_QUERY = "(max-width: 767px), (hover: none)";
function isSheetViewport(): boolean {
  return typeof window !== "undefined" && typeof window.matchMedia === "function" && window.matchMedia(SHEET_QUERY).matches;
}

/** 語を載せている縦に送れる祖先 (本文の送りを持つ箱)。無ければ null (ページ自体が送られる) */
function scrollParentOf(el: HTMLElement | null): HTMLElement | null {
  for (let n = el?.parentElement ?? null; n && n !== document.body && n !== document.documentElement; n = n.parentElement) {
    // 板が開いている間は送りを止めて hidden にしているので、止めた印 (data-gl-locked) が付いた箱も同じ箱として探す
    if (n.dataset.glLocked === "1") return n;
    const o = getComputedStyle(n).overflowY;
    if ((o === "auto" || o === "scroll" || o === "overlay") && n.scrollHeight > n.clientHeight + 1) return n;
  }
  return null;
}

/**
 * 板を置ける範囲。本文を送る箱 (無ければ画面) の中で、上の帯と下の帯にかぶらない所。
 * 上と下の端に貼りついた幅の広い固定の帯 (fixed、sticky) があれば、その内側から始める。
 * 帯が本文の送りの外にある画面 (下の「次の項目へ」の帯など) は、送りの箱の下端で止まる
 */
function sheetBounds(anchor: HTMLElement, card: HTMLElement): { left: number; right: number; top: number; bottom: number } {
  const vw = document.documentElement.clientWidth || window.innerWidth;
  const vh = window.innerHeight;
  const box = scrollParentOf(anchor);
  const r = box ? box.getBoundingClientRect() : null;
  let left = Math.max(0, r?.left ?? 0);
  let right = Math.min(vw, r?.right ?? vw);
  let top = Math.max(0, r?.top ?? 0);
  let bottom = Math.min(vh, r?.bottom ?? vh);
  const wide = (right - left) * 0.8;
  // 帯かどうかは、元の上下の端 (top0、bottom0) に貼りついているかで決める。
  // 見つけた帯の端で端を動かしながら判定すると、隣り合う sticky の要素を次々に帯と見て範囲が潰れる
  const top0 = top;
  const bottom0 = bottom;
  for (const e of Array.from(document.body.querySelectorAll<HTMLElement>("*"))) {
    if (card.contains(e) || e.contains(card)) continue;
    const pos = getComputedStyle(e).position;
    if (pos !== "fixed" && pos !== "sticky") continue;
    const b = e.getBoundingClientRect();
    if (b.width < wide || b.height <= 0 || b.height > 160 || b.right <= left || b.left >= right) continue;
    if (Math.abs(b.top - top0) <= 2 && b.bottom > top && b.bottom < bottom0) top = b.bottom;
    else if (Math.abs(b.bottom - bottom0) <= 2 && b.top < bottom && b.top > top0) bottom = b.top;
  }
  // 範囲が潰れたときは、帯を無視して画面全体を使う
  if (bottom - top < 120) {
    top = 0;
    bottom = vh;
  }
  return { left, right, top, bottom };
}

// 本文の送りを止める印。窓が 2 つ重なって開閉しても、最後の窓が閉じるまで止め、元の値へ戻すのも 1 度だけにする
const scrollLocks = new Map<HTMLElement, { count: number; prev: string }>();
function lockScroll(el: HTMLElement, prop: "overflowY" | "overflow"): () => void {
  const held = scrollLocks.get(el);
  if (held) held.count++;
  else {
    scrollLocks.set(el, { count: 1, prev: el.style[prop] });
    el.style[prop] = "hidden";
    el.dataset.glLocked = "1";
  }
  let released = false;
  return () => {
    if (released) return;
    released = true;
    const h = scrollLocks.get(el);
    if (!h) return;
    h.count--;
    if (h.count > 0) return;
    el.style[prop] = h.prev;
    delete el.dataset.glLocked;
    scrollLocks.delete(el);
  };
}

const GlossaryCard = memo(function GlossaryCard(props: CardProps) {
  const { id, titleId, term, matched, reading, aliases, category, difficulty, summary, detail, related, relatedTerms, slug, closing, anchorRef, onClose } = props;
  const env = useContext(GlossaryCardEnvContext);
  const phrase = env.phrase ?? ((children: ReactNode) => children);
  // 題の絵が正式ロゴか考え方の印の語だけ、ふくろうを添える (印の無い語は今のマスコットのまま)
  const marked = useMemo(() => Boolean(inlineLogoForTerm(term, matched) || conceptMarkForTerm(term, matched)), [term, matched]);
  const ref = useRef<HTMLSpanElement>(null);
  const [entered, setEntered] = useState(false);
  const [sheet, setSheet] = useState(isSheetViewport);

  // 位置。語のすぐ下 (入らなければ上) に、画面の右端と下端からはみ出さないよう置く。
  // 祖先に transform があって fixed の原点がずれていても合うよう、left 0 top 0 に置いた実測から原点のずれを引く
  const place = useCallback(() => {
    const card = ref.current;
    const anchor = anchorRef.current;
    if (!card || !anchor) return;
    if (isSheetViewport()) {
      // 板。本文を送る箱の下端 (下の帯の上) に貼り、高さは画面の 70% まで、上の帯にはかぶせない。
      // 祖先に transform があって fixed の原点がずれていても合うよう、PC と同じに原点のずれを引く
      const area = sheetBounds(anchor, card);
      const vh0 = window.innerHeight;
      card.style.bottom = "auto";
      card.style.width = `${area.right - area.left}px`;
      card.style.maxHeight = `${Math.min(vh0 * 0.7, area.bottom - area.top)}px`;
      card.style.left = "0px";
      card.style.top = "0px";
      // 出る前の下げ (translate) を含めずに、原点のずれだけを測る。動き (transition) を止めてから外し、測ったあとに戻す
      card.style.transition = "none";
      card.style.translate = "0 0";
      const origin = card.getBoundingClientRect();
      card.style.translate = "";
      void card.offsetHeight;
      card.style.transition = "";
      card.style.left = `${area.left - origin.left}px`;
      card.style.top = `${area.bottom - origin.height - origin.top}px`;
      card.dataset.placement = "sheet";
      card.dataset.ready = "1";
      setSheet(true);
      return;
    }
    setSheet(false);
    card.style.bottom = "auto";
    const edge = 12;
    const gap = 8;
    const vw = document.documentElement.clientWidth || window.innerWidth;
    const vh = window.innerHeight;
    const width = Math.min(360, vw - edge * 2);
    const hardMax = Math.min(vh - edge * 2, 560);
    card.style.width = `${width}px`;
    card.style.maxHeight = `${hardMax}px`;
    card.style.left = "0px";
    card.style.top = "0px";
    const origin = card.getBoundingClientRect();
    let height = origin.height;
    const rects = anchor.getClientRects();
    const rect = rects.length > 0 ? rects[0] : anchor.getBoundingClientRect();
    const below = vh - rect.bottom - gap - edge;
    const above = rect.top - gap - edge;
    const placeAbove = height > below && above > below;
    const room = placeAbove ? above : below;
    if (height > room) {
      card.style.maxHeight = `${Math.max(Math.min(room, hardMax), Math.min(220, hardMax))}px`;
      height = card.getBoundingClientRect().height;
    }
    let top = placeAbove ? rect.top - gap - height : rect.bottom + gap;
    top = Math.min(Math.max(top, edge), Math.max(edge, vh - edge - height));
    const left = Math.min(Math.max(rect.left, edge), Math.max(edge, vw - edge - width));
    card.style.left = `${left - origin.left}px`;
    card.style.top = `${top - origin.top}px`;
    card.style.setProperty("--gl-origin-x", `${Math.min(Math.max(rect.left + rect.width / 2 - left, 16), width - 16)}px`);
    card.dataset.placement = placeAbove ? "above" : "below";
    card.dataset.ready = "1";
  }, [anchorRef]);

  useIsoLayoutEffect(() => {
    place();
  }, [place, term, detail, related]);

  useEffect(() => {
    let raf = 0;
    const again = (e?: Event) => {
      // 窓の中を送っているだけなら、置き直さない
      if (e?.target instanceof Node && ref.current?.contains(e.target)) return;
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(place);
    };
    window.addEventListener("resize", again);
    window.addEventListener("scroll", again, true);
    // 出す動きは位置が決まった次のフレームから始める
    const start = requestAnimationFrame(() => setEntered(true));
    return () => {
      cancelAnimationFrame(raf);
      cancelAnimationFrame(start);
      window.removeEventListener("resize", again);
      window.removeEventListener("scroll", again, true);
    };
  }, [place]);

  // 板が開いている間は、本文の送りを止める (板の中だけが縦に送れる)
  useEffect(() => {
    if (!sheet) return;
    const box = scrollParentOf(anchorRef.current);
    const releaseBox = box ? lockScroll(box, "overflowY") : null;
    const releaseHtml = lockScroll(document.documentElement, "overflow");
    return () => {
      releaseBox?.();
      releaseHtml();
    };
  }, [sheet, anchorRef]);

  const { english, other } = useMemo(() => splitAliases(term, aliases), [term, aliases]);
  // 詳しい説明が 1 行イメージ (summary) の言い直しから始まるときは、重ねて出さない
  const clipped = useMemo(() => {
    let body = detail?.trim() ?? "";
    const head = summary?.trim() ?? "";
    if (head && body.startsWith(head)) body = body.slice(head.length).replace(/^[\s。.]+/, "").trim();
    return body ? clipDetail(body) : null;
  }, [detail, summary]);

  // 関連章は、学習パスの章の名前に直す。引けない章は出さず、同じ章はまとめる
  const chapters = useMemo(() => {
    if (!env.chapterOf) return [];
    const seen = new Set<string>();
    const out: GlossaryChapterRef[] = [];
    for (const n of parseChapterNumbers(related)) {
      const ref = env.chapterOf(n);
      if (!ref || seen.has(ref.label)) continue;
      seen.add(ref.label);
      out.push(ref);
    }
    return out;
  }, [env, related]);

  const label = difficulty === 1 ? "初級" : difficulty === 2 ? "中級" : difficulty === 3 ? "上級" : null;

  return (
    <span
      ref={ref}
      id={id}
      role="dialog"
      aria-modal="false"
      aria-labelledby={titleId}
      className="gl-card"
      data-show={entered && !closing ? "true" : "false"}
      data-mode={sheet ? "sheet" : undefined}
    >
      <span className="gl-card__head">
        <Mark name={term} matched={matched} />
        <span className="gl-card__title-wrap">
          <span className="gl-card__title" id={titleId}>
            {term}
          </span>
          {reading ? <span className="gl-card__reading">{reading}</span> : null}
        </span>
        {marked ? <Owl size={28} /> : null}
        {sheet ? (
          <button type="button" className="gl-card__close" aria-label={`${term} の窓を閉じる`} onClick={onClose}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <line x1="6" y1="6" x2="18" y2="18" />
              <line x1="18" y1="6" x2="6" y2="18" />
            </svg>
          </button>
        ) : null}
      </span>

      {category || label ? (
        <span className="gl-card__tags">
          {category ? <span className="gl-card__tag">{category}</span> : null}
          {label ? (
            <span className="gl-card__tag gl-card__tag--level" data-level={difficulty}>
              {label}
            </span>
          ) : null}
        </span>
      ) : null}

      {summary ? <span className="gl-card__summary">{phrase(summary)}</span> : null}

      {english.length > 0 || other.length > 0 ? (
        <span className="gl-card__aka">
          {english.length > 0 ? (
            <span className="gl-card__aka-row">
              <span className="gl-card__aka-label">英語の綴り</span>
              <span className="gl-card__aka-value">{english.join("、")}</span>
            </span>
          ) : null}
          {other.length > 0 ? (
            <span className="gl-card__aka-row">
              <span className="gl-card__aka-label">別の呼び方</span>
              <span className="gl-card__aka-value">{other.join("、")}</span>
            </span>
          ) : null}
        </span>
      ) : null}

      {clipped ? (
        <span className="gl-card__detail">
          <DetailBody markdown={clipped.text} />
          {clipped.clipped ? <span className="gl-card__clip">…</span> : null}
        </span>
      ) : null}

      {relatedTerms.length > 0 ? (
        <span className="gl-card__block">
          <span className="gl-card__label">関連用語</span>
          <span className="gl-card__chips">
            {relatedTerms.map((t) =>
              t.slug && !env.stayInPlace ? (
                <a key={t.name} className="gl-card__chip" href={`/glossary/${encodeURIComponent(t.slug)}`}>
                  <ChipMark name={t.name} />
                  {t.name}
                </a>
              ) : (
                <span key={t.name} className="gl-card__chip gl-card__chip--plain">
                  <ChipMark name={t.name} />
                  {t.name}
                </span>
              ),
            )}
          </span>
        </span>
      ) : null}

      {chapters.length > 0 ? (
        <span className="gl-card__block">
          <span className="gl-card__label">出てくる章</span>
          <span className="gl-card__chips">
            {chapters.map((c) =>
              c.href ? (
                <a key={c.label} className="gl-card__chip" href={c.href}>
                  {c.label}
                </a>
              ) : (
                <span key={c.label} className="gl-card__chip gl-card__chip--plain">
                  {c.label}
                </span>
              ),
            )}
          </span>
        </span>
      ) : null}

      {slug ? (
        <span className="gl-card__foot">
          <a
            className="gl-card__more lg-term-more"
            href={`/glossary/${encodeURIComponent(slug)}`}
            aria-label={env.stayInPlace ? `${term} の詳細ページを新しいタブで開く` : `${term} の詳細ページを開く`}
            {...(env.stayInPlace ? { target: "_blank", rel: "noopener noreferrer" } : {})}
          >
            くわしく見る
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <polyline points="9 18 15 12 9 6" />
            </svg>
          </a>
        </span>
      ) : null}
    </span>
  );
});

// ---------------------------------------------------------------------------
// 本文の語
// ---------------------------------------------------------------------------

/** 用語ハイライト (rehypeGlossary が <glossary-term> に変換した部分を描画) */
export const GlossaryTermInline = memo(function GlossaryTermInline({
  term,
  matched,
  summary,
  related,
  detail,
  reading,
  category,
  difficulty,
  slug,
  relatedTermsRaw,
  aliasesRaw,
  children,
}: {
  term?: string;
  matched?: string;
  summary?: string;
  related?: string;
  detail?: string;
  reading?: string;
  category?: string;
  difficulty?: number;
  /** 公開用語集 (/glossary/:slug) の slug。設定時は語と窓の「くわしく見る」がその頁へ飛ぶ */
  slug?: string;
  /** "用語@@slug|用語" の形 (slug が無い用語は名前だけ) */
  relatedTermsRaw?: string;
  /** 別名を "a|b" でつないだ物 */
  aliasesRaw?: string;
  children?: ReactNode;
}) {
  const name = term ?? matched ?? "用語";
  const { stayInPlace = false } = useContext(GlossaryCardEnvContext);
  const [phase, setPhaseState] = useState<Phase>("closed");
  const phaseRef = useRef<Phase>("closed");
  const pinned = useRef(false);
  const skipFocusOpen = useRef(false);
  const timers = useRef<{ open?: number; close?: number; unmount?: number }>({});
  const wrapRef = useRef<HTMLSpanElement>(null);
  const wordRef = useRef<HTMLElement | null>(null);
  const reactId = useId();
  const cardId = `gl-card-${reactId}`;
  const titleId = `gl-card-title-${reactId}`;
  const relatedTerms = useMemo(() => parseRelatedTerms(relatedTermsRaw), [relatedTermsRaw]);

  const go = useCallback((next: Phase) => {
    phaseRef.current = next;
    setPhaseState(next);
  }, []);

  const clearAll = useCallback(() => {
    window.clearTimeout(timers.current.open);
    window.clearTimeout(timers.current.close);
    window.clearTimeout(timers.current.unmount);
  }, []);

  const requestOpen = useCallback(
    (delay: number) => {
      window.clearTimeout(timers.current.close);
      window.clearTimeout(timers.current.unmount);
      if (phaseRef.current === "open") return;
      window.clearTimeout(timers.current.open);
      if (delay <= 0) go("open");
      else timers.current.open = window.setTimeout(() => go("open"), delay);
    },
    [go],
  );

  const requestClose = useCallback(
    (delay: number, force = false) => {
      window.clearTimeout(timers.current.open);
      if (pinned.current && !force) return;
      if (phaseRef.current === "closed") return;
      window.clearTimeout(timers.current.close);
      const run = () => {
        pinned.current = false;
        go("closing");
        timers.current.unmount = window.setTimeout(() => go("closed"), UNMOUNT_AFTER);
      };
      if (delay <= 0) run();
      else timers.current.close = window.setTimeout(run, delay);
    },
    [go],
  );

  useEffect(() => clearAll, [clearAll]);

  // 開いている間だけ、Esc と、語と窓の外を押したときの閉じる動きを付ける
  useEffect(() => {
    if (phase === "closed") return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      requestClose(0, true);
      // 窓の中のリンクに focus があったなら語へ戻す。戻したときの focus で窓がまた開かないようにする
      const active = document.activeElement;
      if (active && active !== wordRef.current && wrapRef.current?.contains(active)) {
        skipFocusOpen.current = true;
        wordRef.current?.focus({ preventScroll: true });
      }
    };
    const onDown = (e: PointerEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) requestClose(0, true);
    };
    document.addEventListener("keydown", onKey);
    document.addEventListener("pointerdown", onDown);
    return () => {
      document.removeEventListener("keydown", onKey);
      document.removeEventListener("pointerdown", onDown);
    };
  }, [phase, requestClose]);

  const hasHover = (e: React.PointerEvent) => e.pointerType === "mouse" || e.pointerType === "pen";

  const setWord = useCallback((el: HTMLElement | null) => {
    wordRef.current = el;
  }, []);
  const aliases = useMemo(() => (aliasesRaw ? aliasesRaw.split("|").filter(Boolean) : []), [aliasesRaw]);

  return (
    <span
      ref={wrapRef}
      className="gl-term"
      data-open={phase === "open" ? "true" : undefined}
      onPointerEnter={(e) => {
        if (hasHover(e)) requestOpen(OPEN_DELAY);
      }}
      onPointerLeave={(e) => {
        if (hasHover(e)) requestClose(CLOSE_DELAY);
      }}
      onFocus={(e) => {
        // マウスやタップで当たった focus では開かない (キーボードで来たときだけ)
        if (skipFocusOpen.current) {
          skipFocusOpen.current = false;
          return;
        }
        const target = e.target as HTMLElement;
        if (target.matches?.(":focus-visible")) requestOpen(OPEN_DELAY / 2);
      }}
      onBlur={(e) => {
        const next = e.relatedTarget as Node | null;
        if (!next || !wrapRef.current?.contains(next)) requestClose(CLOSE_DELAY);
      }}
    >
      {slug ? (
        <a
          className="gl-term__word"
          ref={setWord}
          href={`/glossary/${encodeURIComponent(slug)}`}
          onClick={(e) => {
            e.stopPropagation();
            // スマホと hover の無い端末、stayInPlace の所は、押すと外の頁へ飛ばずに窓を開く (外枠と読んでいた位置を失わない)。くわしくは窓の「くわしく見る」から
            if (!stayInPlace && !isSheetViewport()) return;
            // 新しいタブで開く押し方 (Cmd、Ctrl、Shift、中の釦) は、そのまま辞書の頁へ
            if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) return;
            e.preventDefault();
            if (stayInPlace && phase === "open" && pinned.current) {
              requestClose(0, true);
              return;
            }
            pinned.current = true;
            requestOpen(0);
          }}
          aria-label={stayInPlace ? `${name} の意味を見る` : `${name} の詳細ページを開く`}
          aria-haspopup={stayInPlace ? "dialog" : undefined}
          aria-expanded={stayInPlace ? phase === "open" : undefined}
          aria-describedby={phase === "open" ? cardId : undefined}
        >
          {children}
        </a>
      ) : (
        <span className="gl-term__word" ref={setWord} tabIndex={0} aria-describedby={phase === "open" ? cardId : undefined}>
          {children}
        </span>
      )}
      <button
        type="button"
        className="gl-term__q"
        aria-label={`${name} の意味を見る`}
        aria-expanded={phase === "open"}
        aria-haspopup="dialog"
        aria-controls={phase === "closed" ? undefined : cardId}
        onClick={(e) => {
          e.stopPropagation();
          if (phase === "open" && pinned.current) {
            requestClose(0, true);
          } else {
            // 開いていても、押したら固定して外すまで残す
            pinned.current = true;
            requestOpen(0);
          }
        }}
      >
        ?
      </button>
      {phase !== "closed" ? (
        <GlossaryCard
          id={cardId}
          titleId={titleId}
          term={name}
          matched={matched}
          reading={reading || undefined}
          aliases={aliases}
          category={category || undefined}
          difficulty={difficulty}
          summary={summary || undefined}
          detail={detail || undefined}
          related={related || undefined}
          relatedTerms={relatedTerms}
          slug={slug}
          closing={phase === "closing"}
          anchorRef={wordRef}
          onClose={() => {
            requestClose(0, true);
            // 閉じたあとの focus は語へ戻す (戻した focus で窓がまた開かないようにする)
            skipFocusOpen.current = true;
            wordRef.current?.focus({ preventScroll: true });
          }}
        />
      ) : null}
    </span>
  );
});
